import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { CloseIcon } from '@/components/FilterSheet'
import { usePageLoader } from '@/components/LogoLoader'
import { fmtDate, fmtNumber, useT } from '@/i18n'
import { crewsMessages } from '@/i18n/messages/crews'
import { Badge } from './Badge'
import { CrewMap } from './CrewMap'
import type { CrewPhoto, CrewRoute } from './types'

/**
 * Where the crew worked and what it looked like: its RTK routes on a replayable map, and its
 * latest site photos. Most crews record no RTK, so the map says so plainly; report photos
 * exist for any crew that files Aufmaß reports.
 */
export function MapPhotosTab({ crewId }: { crewId: string }) {
  const t = useT(crewsMessages)
  const routes = useQuery<CrewRoute[]>({
    queryKey: ['crew-routes', crewId],
    queryFn: () => api.get<CrewRoute[]>(`/api/crews/${crewId}/routes`),
    staleTime: 5 * 60_000,
  })
  const photos = useQuery<CrewPhoto[]>({
    queryKey: ['crew-photos', crewId],
    queryFn: () => api.get<CrewPhoto[]>(`/api/crews/${crewId}/photos`),
    staleTime: 5 * 60_000,
  })

  const loader = usePageLoader(routes.isPending, t('loadingMap'))
  if (loader) return loader

  const r = routes.data ?? []
  const metres = r.reduce((s, x) => s + (x.geojson.type === 'LineString' ? x.length_m ?? 0 : 0), 0)
  const projects = new Set(r.map((x) => x.project_id).filter(Boolean)).size
  const first = r[0]?.measured_at
  const last = r[r.length - 1]?.measured_at

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold">{t('mapTitle')}</h2>
          {r.length > 0 && (
            <span className="text-xs text-neutral-500">
              {t('mapSummary', { n: r.length, metres: fmtNumber(metres), count: projects })} · {fmtDate(first)} – {fmtDate(last)}
            </span>
          )}
        </div>
        {r.length > 0 ? (
          <CrewMap routes={r} />
        ) : (
          <div className="flex items-start gap-3 rounded-2xl bg-neutral-100 p-4 text-sm text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
            <span aria-hidden className="text-lg">⌀</span>
            <p>{t('mapNoRtk')}</p>
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t('photosTitle')}</h2>
        <p className="text-xs text-neutral-500">{t('photosHint')}</p>
        <PhotoGrid photos={photos.data ?? []} loading={photos.isPending} />
      </section>
    </div>
  )
}

function PhotoGrid({ photos, loading }: { photos: CrewPhoto[]; loading: boolean }) {
  const t = useT(crewsMessages)
  const [open, setOpen] = useState<number | null>(null)

  useEffect(() => {
    if (open == null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null)
      if (e.key === 'ArrowRight') setOpen((i) => (i == null ? i : Math.min(i + 1, photos.length - 1)))
      if (e.key === 'ArrowLeft') setOpen((i) => (i == null ? i : Math.max(i - 1, 0)))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, photos.length])

  if (loading) return <div className="h-28 animate-pulse rounded-2xl bg-neutral-100 dark:bg-neutral-800" />
  if (photos.length === 0) return <p className="text-sm text-neutral-500">{t('noPhotos')}</p>

  const p = open != null ? photos[open] : null
  return (
    <>
      <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
        {photos.map((ph, i) => (
          <li key={ph.thumbUrl}>
            <button type="button" onClick={() => setOpen(i)} className="group block w-full text-left">
              <img src={ph.thumbUrl} alt={ph.caption ?? ''} loading="lazy"
                className="aspect-square w-full rounded-xl bg-neutral-100 object-cover ring-1 ring-neutral-200 group-hover:ring-brand-500 dark:bg-neutral-800 dark:ring-neutral-800" />
              <span className="mt-1 block truncate text-[11px] text-neutral-500">{fmtDate(ph.day)} · {ph.project ?? '—'}</span>
            </button>
          </li>
        ))}
      </ul>
      {p && createPortal(
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-[100] flex flex-col bg-black/90 p-3 sm:p-6" onClick={() => setOpen(null)}>
          <div className="flex items-start justify-between gap-3 text-sm text-white">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={p.kind === 'rtk' ? 'green' : 'grey'}>{p.kind === 'rtk' ? 'RTK' : t('photoReport')}</Badge>
                <span>{fmtDate(p.day)} · {p.project ?? '—'}</span>
                <span className="text-white/50">{(open ?? 0) + 1} / {photos.length}</span>
              </div>
              {p.caption && <p className="mt-1 text-xs text-white/70">{p.caption}</p>}
            </div>
            <button type="button" aria-label={t('closePhoto')} onClick={() => setOpen(null)} className="rounded-full p-2 hover:bg-white/10">
              <CloseIcon />
            </button>
          </div>
          <div className="flex min-h-0 flex-1 items-center justify-center gap-2 pt-3" onClick={(e) => e.stopPropagation()}>
            <button type="button" aria-label="←" disabled={open === 0} onClick={() => setOpen((i) => Math.max((i ?? 0) - 1, 0))}
              className="rounded-full p-3 text-2xl text-white disabled:opacity-20">‹</button>
            <img src={p.url} alt={p.caption ?? ''} className="max-h-full min-w-0 max-w-full rounded-lg object-contain" />
            <button type="button" aria-label="→" disabled={open === photos.length - 1} onClick={() => setOpen((i) => Math.min((i ?? 0) + 1, photos.length - 1))}
              className="rounded-full p-3 text-2xl text-white disabled:opacity-20">›</button>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

